import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import i18n from '@nextroll/ar-i18n';
import { Option, Select } from '@nextroll/ar-style-base';

import { WorkflowBaseType, WORKFLOW_BASE } from 'types/rofl';
import { WORKFLOW_SCHEDULE_DEFAULT, WORKFLOW_SCHEDULE_DAYS_OF_WEEK } from 'util/workflow.constants';


interface IProps {
    base?: WorkflowBaseType;
    readonly?: boolean;
    schedule: string;
    setSchedule: Dispatch<SetStateAction<string>>;
}

const DAYS_OF_WEEK_DISPLAY: { [weekday: string]: string } = {
    SUN: i18n.gettext('Sunday'),
    MON: i18n.gettext('Monday'),
    TUE: i18n.gettext('Tuesday'),
    WED: i18n.gettext('Wednesday'),
    THU: i18n.gettext('Thursday'),
    FRI: i18n.gettext('Friday'),
    SAT: i18n.gettext('Saturday'),
};

export const SettingsSectionSchedule = (props: IProps) => {
    const { base, readonly, schedule, setSchedule } = props;

    // local weekday value for rendering the select
    const [dayOfWeek, setDayOfWeek] = useState('MON');

    /**
     * sync the local weekday selection with the external data
     */
    useEffect(() => {
        const updatedDayOfWeek = Object.entries(WORKFLOW_SCHEDULE_DAYS_OF_WEEK)
            .find(([_, exp]) => exp === schedule)?.[0];
        if (updatedDayOfWeek && updatedDayOfWeek !== dayOfWeek) {
            setDayOfWeek(updatedDayOfWeek);
        }
    }, [schedule, dayOfWeek]);

    /**
     * description text above the options
     */
    const description = useMemo(() => {
        if (base === WORKFLOW_BASE.ACCOUNTS) {
            return readonly
                ? i18n.gettext('The workflow will enroll accounts who meet the trigger criteria:')
                : i18n.gettext('After turning on, how often should this workflow enroll accounts who meet the trigger criteria?');
        }
        return readonly
            ? i18n.gettext('The workflow will enroll contacts who meet the trigger criteria:')
            : i18n.gettext('After turning on, how often should this workflow enroll contacts who meet the trigger criteria?');
    }, [base, readonly]);

    /**
     * weekday select element
     */
    const select = useMemo(() => (
        <Select
            i18n={i18n}
            onOptionSelect={(day: string) => {
                setDayOfWeek(day);
                setSchedule(WORKFLOW_SCHEDULE_DAYS_OF_WEEK[day]);
            }}
        >
            {Object.entries(WORKFLOW_SCHEDULE_DAYS_OF_WEEK).map(([day]) => (
                <Option
                    key={`workflows-canvas-setting-panel-enrollment-schedule-${day}`}
                    value={day}
                    selected={day === dayOfWeek}
                >
                    {DAYS_OF_WEEK_DISPLAY[day]}
                </Option>
            ))}
        </Select>
    ), [dayOfWeek, setDayOfWeek, setSchedule]);

    return (
        <>
            <p><strong>{i18n.gettext('Enrollment Schedule')}</strong></p>
            <p>{description}</p>

            <input
                type='radio'
                id='workflows-canvas-setting-panel-enrollment-schedule-hour'
                className='ar-radio-button'
                value='true'
                checked={schedule === WORKFLOW_SCHEDULE_DEFAULT}
                onChange={() => setSchedule(WORKFLOW_SCHEDULE_DEFAULT)}
            />
            <label htmlFor='workflows-canvas-setting-panel-enrollment-schedule-hour'>
                {i18n.gettext('Once every 6 hours')}
            </label>
            <br />
            <input
                type='radio'
                id='workflows-canvas-setting-panel-enrollment-schedule-day-of-week'
                className='ar-radio-button'
                value='false'
                checked={schedule !== WORKFLOW_SCHEDULE_DEFAULT}
                onChange={() => setSchedule(WORKFLOW_SCHEDULE_DAYS_OF_WEEK[dayOfWeek])}
            />
            <label htmlFor='workflows-canvas-setting-panel-enrollment-schedule-day-of-week'>
                {readonly ? i18n.gettext('Once every week on') : i18n.gettext('Run immediately and then every week on')}
                &nbsp;&nbsp;
                {select}
            </label>
        </>
    );
};

export default SettingsSectionSchedule;
